import type { ApiRequestParams, ApiReturnType, ApiSchemas } from '$lib/api/shopwareApi.js';
import { toast as toastStore } from '$lib/stores.js';
import {
    CART_INVALID_PAYMENT_METHOD_SELECTED,
    CART_INVALID_QUANTITY,
    CART_PRODUCT_OUT_OF_STOCK,
    CART_PRODUCT_STOCK_REACHED,
    CART_PROMOTION_NOT_APPLICABLE,
    CART_PROMOTION_NOT_FOUND
} from '$lib/util/shopwareErrorCodes.js';
import type { CreateBaseMutationResult } from '@tanstack/svelte-query';
import { format as formatStore } from 'svelte-i18n';
import { get } from 'svelte/store';
import posthog from 'posthog-js';

// Helper function to send Meta AddToCart event via server endpoint
async function sendMetaAddToCartEvent(
    productId: string,
    productName: string | undefined,
    quantity: number | undefined,
    price: number | undefined,
    currency: string | undefined
) {
    try {
        await fetch(`/api/add-to-cart`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                productId,
                productName,
                quantity,
                price,
                currency
            })
        });
    } catch (error) {
        console.error('Failed to send Meta AddToCart event:', error);
    }
}

export async function executeAddToCart(
    productId: string,
    cart: ApiSchemas['Cart'],
    addToCartMutation: CreateBaseMutationResult<ApiReturnType<'addLineItem'>, unknown, ApiRequestParams<'addLineItem'>>,
    updateCartMutation: CreateBaseMutationResult<
        ApiReturnType<'updateLineItem'>,
        unknown,
        ApiRequestParams<'updateLineItem'>
    >,
    {
        updateExistingLineItem = false,
        type = 'product',
        productName = undefined,
        price = undefined,
        currency = undefined,
        language = undefined
    }: {
        updateExistingLineItem?: boolean;
        type?: 'product' | 'promotion';
        productName?: string;
        price?: number;
        currency?: string;
        language?: string;
    } = {},
    quantity?: number
): Promise<void> {
    const toast = get(toastStore);
    const t = get(formatStore);

    let response: any;
    try {
        const existingLineItem = cart?.lineItems?.find(
            (lineItem: ApiSchemas['LineItem']) => lineItem.referencedId === productId
        );

        if (existingLineItem && !updateExistingLineItem) {
            return;
        }

        if (existingLineItem) {
            response = await updateCartMutation.mutateAsync({
                items: [
                    {
                        type,
                        id: existingLineItem.id,
                        referencedId: productId,
                        quantity: (existingLineItem.quantity ?? 0) + (quantity ?? 1)
                    }
                ]
            });
        } else {
            response = await addToCartMutation.mutateAsync({
                items: [
                    {
                        type,
                        referencedId: productId,
                        quantity: quantity ?? 1
                    }
                ]
            });
        }

        const errors = response.errors as unknown as Record<
            string,
            {
                message: string;
                code: number;
                key: string;
                level: number;
                messageKey: string;
            }
        >;

        const promotionAddedMessage = Object.values(errors).find((e) => e.messageKey === 'promotion-discount-added');
        if (promotionAddedMessage) {
            delete errors[promotionAddedMessage.key];
        }

        if (Object.values(errors).length) {
            for (const error of Object.values(errors)) {
                if (error.messageKey === CART_PRODUCT_STOCK_REACHED) {
                    toast?.showError(t('shop.cart.productAddingFailedStockReached'));
                } else if (error.messageKey === CART_INVALID_QUANTITY) {
                    toast?.showError(t('shop.cart.productAddingFailedInvalidQuantity'));
                } else if (error.messageKey === CART_PRODUCT_OUT_OF_STOCK) {
                    toast?.showError(t('shop.cart.productAddingFailedOutOfStock'));
                } else if (
                    error.messageKey === CART_PROMOTION_NOT_APPLICABLE ||
                    error.messageKey === CART_PROMOTION_NOT_FOUND
                ) {
                    toast?.showError(t('shop.cart.productAddingFailedPromotionNotApplicable'));
                } else if (error.messageKey === CART_INVALID_PAYMENT_METHOD_SELECTED) {
                    toast?.showError(t('shop.cart.productAddingFailedInvalidPaymentMethod'));
                } else {
                    toast?.showError(t('shop.cart.productAddingFailed'));
                }
            }
        } else {
            if (type === 'promotion') {
                toast?.showSuccess(t('shop.cart.promotionAdded'));
            } else {
                posthog.capture('Add to cart', {
                    productId: productId,
                    quantity: quantity
                });
                toast?.showSuccess(t('shop.cart.productAdded'));
            }

            if (type === 'product') {
                window.fbq?.('track', 'AddToCart', {
                    content_type: 'product',
                    content_name: productName,
                    content_ids: productId ? [productId] : undefined,
                    contents: [{ id: productId, quantity: 1 }]
                });

                // Send Meta Conversions API event via server
                try {
                    await sendMetaAddToCartEvent(productId, productName, quantity, price, currency);
                } catch (err) {
                    console.error(err);
                }
            }
        }
    } catch (error) {
        console.error(error);

        if (type === 'promotion') {
            toast?.showError(t('shop.cart.promotionAddingFailed'));
        } else {
            toast?.showError(t('shop.cart.productAddingFailed'));
        }
    }
}
